import { BooleanInput } from '@angular/cdk/coercion';
import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroupDirective, NgControl } from '@angular/forms';
import { Editor } from '@tiptap/core';
import {
  LucideBold,
  LucideHeading2,
  LucideItalic,
  LucideLink,
  LucideList,
  LucideListOrdered,
  LucideIcon,
  LucideMaximize2,
  LucideMinimize2,
  LucideStrikethrough,
} from '@lucide/angular';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { TextColor, TextDirective, TextSize } from '../../typography/text.directive';
import { ButtonComponent } from '../button/button.component';
import { InlineButtonComponent } from '../inline-button/inline-button.component';
import { createRichTextExtensions } from './rich-text-extensions';
import { isRichTextHTML, sanitizeRichTextHTML } from './rich-text-sanitizer';

export type JoiiRichTextOutputType = 'markdown' | 'html';
export type JoiiRichTextToolbarItem = 'heading' | 'bold' | 'italic' | 'strike' | 'bulletList' | 'orderedList' | 'link';

export const JOII_RICH_TEXT_TOOLBAR_ITEMS: JoiiRichTextToolbarItem[] = [
  'heading',
  'bold',
  'italic',
  'strike',
  'bulletList',
  'orderedList',
  'link',
];

type ToolbarButton = { item: JoiiRichTextToolbarItem; label: string; icon: LucideIcon };

const TOOLBAR_BUTTONS: ToolbarButton[] = [
  { item: 'heading', label: 'Heading', icon: LucideHeading2 },
  { item: 'bold', label: 'Bold', icon: LucideBold },
  { item: 'italic', label: 'Italic', icon: LucideItalic },
  { item: 'strike', label: 'Strikethrough', icon: LucideStrikethrough },
  { item: 'bulletList', label: 'Bulleted list', icon: LucideList },
  { item: 'orderedList', label: 'Numbered list', icon: LucideListOrdered },
  { item: 'link', label: 'Link', icon: LucideLink },
];

@Component({
  selector: 'joii-rich-text',
  imports: [ButtonComponent, InlineButtonComponent, TextDirective],
  providers: [provideValueAccessor(RichTextComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (showLabel()) {
      <label
        class="joii-rich-text-label"
        joii-text
        [id]="labelId()"
        [size]="2"
        [color]="labelTextColor()"
        [lineHeight]="'14'"
        letterSpacing="8-16"
        >{{ labelText() }}</label
      >
    }

    @if (toolbar()) {
      <div
        class="joii-rich-text-toolbar"
        role="toolbar"
        [attr.aria-label]="(label() || 'Text') + ' formatting'"
        [style.--joii-rich-text-toolbar-items]="toolbarButtons().length + 1"
      >
        @for (button of toolbarButtons(); track button.item) {
          <button
            joii-inline-button
            type="button"
            variant="secondary"
            size="md"
            class="joii-rich-text-toolbar-button"
            [iconOnly]="true"
            [prefixIcon]="button.icon"
            [active]="toolbarState()[button.item]"
            [disabled]="!canEdit()"
            [attr.aria-label]="button.label"
            [attr.aria-pressed]="toolbarState()[button.item]"
            [attr.title]="button.label"
            (mousedown)="$event.preventDefault()"
            (click)="runToolbarItem(button.item)"
          ></button>
        }
        <div class="joii-rich-text-toolbar-end">
          <ng-content select="[richTextToolbarEnd]" />
          @if (enableFullscreen()) {
            <button
              joii-inline-button
              type="button"
              variant="secondary"
              size="md"
              class="joii-rich-text-toolbar-button"
              [iconOnly]="true"
              [prefixIcon]="fullscreen() ? minimizeIcon : maximizeIcon"
              [active]="fullscreen()"
              [attr.aria-label]="fullscreen() ? 'Exit full screen' : 'Full screen'"
              [attr.aria-pressed]="fullscreen()"
              [attr.title]="fullscreen() ? 'Exit full screen' : 'Full screen'"
              (mousedown)="$event.preventDefault()"
              (click)="toggleFullscreen()"
            ></button>
          }
        </div>
      </div>

      @if (linkEditorOpen()) {
        <div class="joii-rich-text-link-editor">
          <input
            #linkInput
            class="joii-rich-text-link-input"
            type="url"
            placeholder="https://"
            aria-label="Link URL"
            [value]="linkHref()"
            [attr.aria-invalid]="linkError() ? 'true' : null"
            [attr.aria-describedby]="linkError() ? inputId() + '-link-error' : null"
            (input)="linkHref.set(linkInput.value); linkError.set(null)"
            (keydown.enter)="$event.preventDefault(); applyLink()"
            (keydown.escape)="$event.stopPropagation(); closeLinkEditor()"
          />
          <button joii-button type="button" size="sm" (click)="applyLink()">Apply</button>
          @if (toolbarState().link) {
            <button joii-button type="button" size="sm" variant="secondary" (click)="removeLink()">Remove</button>
          }
          <button joii-button type="button" size="sm" variant="secondary" (click)="closeLinkEditor()">Cancel</button>
          @if (linkError()) {
            <span
              class="joii-rich-text-link-error"
              joii-text
              [id]="inputId() + '-link-error'"
              [size]="3"
              color="error"
              >{{ linkError() }}</span
            >
          }
        </div>
      }
    }

    <div class="joii-rich-text-body">
      @if (showPlaceholder()) {
        <span class="joii-rich-text-placeholder" aria-hidden="true">{{ placeholder() }}</span>
      }

      <div #editorElement class="joii-rich-text-editor"></div>
    </div>
  `,
  styles: `
    joii-rich-text {
      position: relative;
      isolation: isolate;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: var(--Vetai-Spacing-2);
      width: 100%;
      min-height: var(--joii-rich-text-min-height);
      max-height: var(--joii-rich-text-max-height);
      border-radius: var(--Vetai-Radius-2, 8px);
      padding: var(--Vetai-Spacing-3);
      border: 1px solid var(--Vetai-Form_controls--Border_Default, #d3cbc5);
      background: var(--Vetai-Form_controls--Background_Default, #fdfdfc);
      outline: none;
      overflow: hidden;
      box-sizing: border-box;
    }

    joii-rich-text[data-fixed-height='true'] {
      height: var(--joii-rich-text-height);
    }

    joii-rich-text[data-fullscreen='true'] {
      position: fixed;
      inset: var(--Vetai-Spacing-6);
      z-index: 1000;
      width: auto;
      height: auto;
      max-height: none;
      box-shadow: 0 0 0 100vmax var(--Vetai-Absolute_packages--Black--Transparent, rgb(0 0 0 / 50%));
    }

    joii-rich-text[data-focused='true'],
    joii-rich-text[data-hovered='true'] {
      border: 1px solid var(--Vetai-Form_controls--Border_Hover, #7aa7d7);
      background: var(--Vetai-Form_controls--Background_Hover, #f9f7f6);
    }

    joii-rich-text[data-active='true'],
    joii-rich-text[data-focused='true'] {
      border: 1px solid var(--Vetai-Form_controls--Border_Active, #1561d3);
      background: var(--Vetai-Form_controls--Background_Hover, #f9f7f6);
    }

    joii-rich-text[data-error='true'] {
      border: 1px solid var(--Vetai-Status_packages--Error--Default, #cc0019);
      background: var(--Vetai-Form_controls--Background_Default, #fdfdfc);
    }

    joii-rich-text[data-error='true'][data-focused='true'],
    joii-rich-text[data-error='true'][data-hovered='true'] {
      border: 1px solid var(--Vetai-Status_packages--Error--Default, #cc0019);
      background: var(--Vetai-Form_controls--Background_Hover, #f9f7f6);
    }

    joii-rich-text[data-error='true'][data-active='true'] {
      border: 1px solid var(--Vetai-Status_packages--Error--Active, #990013);
      background: var(--Vetai-Form_controls--Background_Hover, #f9f7f6);
    }

    joii-rich-text[data-readonly='true'],
    joii-rich-text[data-readonly='true'][data-focused='true'],
    joii-rich-text[data-readonly='true'][data-hovered='true'],
    joii-rich-text[data-readonly='true'][data-active='true'],
    joii-rich-text[data-disabled='true'],
    joii-rich-text[data-disabled='true'][data-focused='true'],
    joii-rich-text[data-disabled='true'][data-hovered='true'],
    joii-rich-text[data-disabled='true'][data-active='true'] {
      border: 1px solid var(--Vetai-Form_controls--Border_Default, #d3cbc5);
      background: var(--Vetai-Backgrounds--Tint_2, #e9e5e2);
      cursor: not-allowed;
    }

    joii-rich-text[data-readonly='true'] .joii-rich-text-label {
      color: var(--Vetai-Form_controls--Placeholder, #5e6779);
      font-kerning: none;
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T2, 10px);
      font-style: normal;
      font-weight: var(--Vetai-Font-Weight-Regular, 400);
      line-height: var(--Vetai-Line-height-14, 14px);
      letter-spacing: var(--Vetai-8-16, 0.35px);
    }

    .joii-rich-text-label {
      pointer-events: none;
      user-select: none;
      font-kerning: none;
    }

    /* A tinted bar across the full width of the field, so it bleeds through the host's padding */
    /* The gaps share whatever width is left after the 40px items (each button plus the end group), between the
       design's 12px and 4px, so a full toolbar fits narrow fields. A container query can't do this: it would need
       size containment, which collapses the field in layouts that size it to its content. The toolbar uses the
       buttons' T5 size so the em-based spacing tokens match theirs. */
    .joii-rich-text-toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      font-size: var(--Vetai-T5, 16px);
      column-gap: clamp(
        var(--Vetai-Spacing-1),
        calc(
          (100% - var(--joii-rich-text-toolbar-items) * var(--Vetai-Spacing-10)) /
            max(var(--joii-rich-text-toolbar-items) - 1, 1)
        ),
        var(--Vetai-Spacing-3)
      );
      row-gap: var(--Vetai-Spacing-1);
      margin: 0 calc(-1 * var(--Vetai-Spacing-3));
      padding: var(--Vetai-Spacing-3);
      background: var(--Vetai-Backgrounds--Tint_1);
    }

    /* Inline button's icon-only sizing targets a class it never sets (leaving it unpadded) and sizes the content box;
       the design is 40px square with the 1px border inside it */
    joii-rich-text .joii-rich-text-toolbar-button[icon-only='true'] {
      padding: calc(var(--Vetai-Spacing-2) - 1px);
    }

    /* Inline button's active border uses a token name that doesn't exist, which drops the border entirely */
    joii-rich-text .joii-rich-text-toolbar-button[data-variant='secondary'][data-state-active='true'] {
      border: 1px solid var(--Vetai-Form_controls--Border_Active);
    }

    .joii-rich-text-toolbar:first-child {
      margin-top: calc(-1 * var(--Vetai-Spacing-3));
    }

    .joii-rich-text-toolbar-end {
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-4);
      margin-left: auto;
    }

    .joii-rich-text-link-editor {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--Vetai-Spacing-2);
    }

    .joii-rich-text-link-error {
      flex-basis: 100%;
    }

    .joii-rich-text-link-input {
      flex: 1 1 12rem;
      min-width: 0;
      padding: var(--Vetai-Spacing-1) var(--Vetai-Spacing-2);
      border: 1px solid var(--Vetai-Form_controls--Border_Default, #d3cbc5);
      border-radius: var(--Vetai-Radius-1, 4px);
      background: var(--Vetai-Form_controls--Background_Default, #fdfdfc);
      color: var(--Vetai-Form_controls--Text_Hover, #0e1625);
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T4, 14px);
    }

    .joii-rich-text-body {
      position: relative;
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
      overflow: hidden;
    }

    .joii-rich-text-editor {
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
      overflow: auto;
    }

    joii-rich-text .ProseMirror {
      min-height: var(--joii-rich-text-editor-min-height);
      outline: none;
      color: var(--Vetai-Form_controls--Text_Hover, #0e1625);
      font-kerning: none;
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T5, 16px);
      font-style: normal;
      font-weight: var(--Vetai-Font-Weight-Regular, 400);
      line-height: var(--Vetai-24, 24px);
      letter-spacing: var(--Vetai-8-16, 0.35px);
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    joii-rich-text .ProseMirror > * + * {
      margin-top: var(--Vetai-Spacing-2);
    }

    joii-rich-text .ProseMirror :is(h1, h2, h3, h4, h5, h6) {
      font-weight: var(--Vetai-Font-Weight-Semibold, 600);
    }

    joii-rich-text .ProseMirror h1 {
      font-size: var(--Vetai-T8, 24px);
      line-height: var(--Vetai-32, 32px);
    }

    joii-rich-text .ProseMirror h2 {
      font-size: var(--Vetai-T7, 20px);
      line-height: var(--Vetai-28, 28px);
    }

    joii-rich-text .ProseMirror :is(h3, h4, h5, h6) {
      font-size: var(--Vetai-T6, 18px);
      line-height: var(--Vetai-24, 24px);
    }

    joii-rich-text .ProseMirror :is(ul, ol) {
      padding-left: var(--Vetai-Spacing-6);
    }

    joii-rich-text .ProseMirror ul {
      list-style: disc;
    }

    joii-rich-text .ProseMirror ol {
      list-style: decimal;
    }

    joii-rich-text .ProseMirror li > p {
      margin: 0;
    }

    joii-rich-text .ProseMirror a {
      color: var(--Vetai-Inline_links_and_info--Default, #1561d3);
      text-decoration: underline;
    }

    joii-rich-text .ProseMirror blockquote {
      padding-left: var(--Vetai-Spacing-3);
      border-left: 1px solid var(--Vetai-Form_controls--Border_Default, #d3cbc5);
      color: var(--Vetai-Texts--Tint_1);
    }

    joii-rich-text .ProseMirror table {
      border-collapse: collapse;
      width: 100%;
    }

    joii-rich-text .ProseMirror :is(th, td) {
      padding: var(--Vetai-Spacing-1) var(--Vetai-Spacing-2);
      border: 1px solid var(--Vetai-Form_controls--Border_Default, #d3cbc5);
      text-align: left;
      vertical-align: top;
    }

    joii-rich-text .ProseMirror th {
      font-weight: var(--Vetai-Font-Weight-Semibold, 600);
    }

    joii-rich-text .ProseMirror img {
      max-width: 100%;
      height: auto;
    }

    joii-rich-text .ProseMirror ul[data-type='taskList'] {
      padding-left: 0;
      list-style: none;
    }

    joii-rich-text .ProseMirror li[data-type='taskItem'] {
      display: flex;
      gap: var(--Vetai-Spacing-2);
    }

    joii-rich-text .ProseMirror code {
      padding: 0 var(--Vetai-Spacing-1);
      border-radius: var(--Vetai-Radius-1, 4px);
      background: var(--Vetai-Backgrounds--Tint_2, #e9e5e2);
    }

    joii-rich-text .joii-rich-text-placeholder {
      position: absolute;
      inset: 0 auto auto 0;
      z-index: 1;
      pointer-events: none;
      user-select: none;
      color: var(--Vetai-Form_controls--Placeholder, #5e6779);
      font-kerning: none;
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T5, 16px);
      font-style: normal;
      font-weight: var(--Vetai-Font-Weight-Regular, 400);
      line-height: var(--Vetai-24, 24px);
      letter-spacing: var(--Vetai-8-16, 0.35px);
    }

    joii-rich-text[data-readonly='true'] .ProseMirror,
    joii-rich-text[data-disabled='true'] .ProseMirror {
      cursor: not-allowed;
    }

    joii-rich-text[data-disabled='true'] .joii-rich-text-editor {
      pointer-events: none;
    }
  `,
  host: {
    '[attr.data-focused]': 'focused() ? "true" : null',
    '[attr.data-hovered]': 'hovered() ? "true" : null',
    '[attr.data-active]': 'isActive() ? "true" : null',
    '[attr.data-readonly]': 'readonly() ? "true" : null',
    '[attr.data-disabled]': 'isDisabled() ? "true" : null',
    '[attr.data-error]': 'hasError ? "true" : null',
    '[attr.data-fullscreen]': 'fullscreen() ? "true" : null',
    '[attr.data-fixed-height]': 'height() !== undefined ? "true" : null',
    '[style.--joii-rich-text-height]': 'height() !== undefined ? height() + "px" : null',
    '[style.--joii-rich-text-min-height]': 'minHeight() + "px"',
    '[style.--joii-rich-text-max-height]': '(height() ?? maxHeight()) + "px"',
    '[style.--joii-rich-text-editor-min-height]': 'Math.max(48, minHeight() - 48) + "px"',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(keydown.escape)': 'fullscreen() && toggleFullscreen()',
  },
})
export class RichTextComponent {
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly inputId = input<string>(`joii-rich-text-${Math.random().toString(36).slice(2, 11)}`);
  readonly value = model<string>('');
  readonly outputType = input<JoiiRichTextOutputType>('markdown');
  readonly height = input<number | undefined>();
  readonly minHeight = input<number>(160);
  readonly maxHeight = input<number>(360);
  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly readonly = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly toolbar = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly toolbarItems = input<JoiiRichTextToolbarItem[]>(JOII_RICH_TEXT_TOOLBAR_ITEMS);
  readonly enableFullscreen = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly errorMessage = input<string | undefined>();
  readonly errorId = input<string>(`joii-rich-text-error-${Math.random().toString(36).slice(2, 11)}`);
  readonly ariaLabel = input<string | undefined>();
  readonly ariaDescribedby = input<string | undefined>();

  readonly touched = output<void>();

  protected readonly maximizeIcon = LucideMaximize2;
  protected readonly minimizeIcon = LucideMinimize2;

  protected readonly editorElement = viewChild<ElementRef<HTMLElement>>('editorElement');
  private readonly linkInput = viewChild<ElementRef<HTMLInputElement>>('linkInput');
  protected readonly formDisabled = signal(false);
  protected readonly focused = signal(false);
  protected readonly hovered = signal(false);
  protected readonly fullscreen = signal(false);
  protected readonly initialized = signal(false);
  protected readonly linkEditorOpen = signal(false);
  protected readonly linkHref = signal('');
  protected readonly linkError = signal<string | null>(null);
  protected readonly toolbarState = signal<Record<JoiiRichTextToolbarItem, boolean>>(emptyToolbarState());
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  protected readonly canEdit = computed(() => !this.isDisabled() && !this.readonly());
  protected readonly isActive = computed(() => this.hasEditorValue(this.value()));
  protected readonly toolbarButtons = computed(() =>
    TOOLBAR_BUTTONS.filter((button) => this.toolbarItems().includes(button.item))
  );
  protected readonly showPlaceholder = computed(
    () => this.placeholder().length > 0 && !this.hasEditorValue(this.value())
  );
  protected readonly showLabel = computed(() => {
    this.controlStatusVersion();
    return this.label().length > 0 || this.hasError;
  });
  protected readonly labelId = computed(() => `${this.inputId()}-label`);
  protected readonly labelText = computed(() => {
    this.controlStatusVersion();
    if (this.hasError && this.displayError()) {
      return this.displayError();
    }
    return this.label();
  });
  protected readonly labelTextSize = computed<TextSize>(() =>
    this.focused() || this.hovered() || this.isActive() ? 2 : 5
  );
  protected readonly labelTextColor = computed<TextColor>(() => {
    this.controlStatusVersion();
    if (this.readonly() || this.isDisabled()) return 'placeholder';
    if (!this.hasError) {
      return this.isActive() ? 'inline-link' : 'placeholder';
    }
    if (this.isActive()) return 'error-active';
    return 'error';
  });

  private readonly injector = inject(Injector);
  private readonly formDirective = inject(FormGroupDirective, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly controlStatusVersion = signal(0);

  private editor?: Editor;
  private onChange?: ChangeFn<string | undefined>;
  protected onTouched?: TouchedFn;
  // The value the editor content was last loaded from or emitted as, so echoes of our own changes are ignored
  private lastSyncedEditorValue = '';

  readonly displayError = computed(() => {
    const msg = this.errorMessage();
    if (msg) return msg;
    this.controlStatusVersion();
    const control = this.injector.get(NgControl, null)?.control;
    if (!control?.errors) return '';
    return this.getControlErrorMessageFromErrors(control.errors);
  });

  get hasError(): boolean {
    const control = this.injector.get(NgControl, null)?.control;
    const message = this.displayError();
    if (!control) return message.length > 0;
    const showError = this.getShowError(
      this.focused(),
      control,
      this.formDirective ?? undefined,
      this.label().length === 0
    );
    return !!(control.invalid && showError && message.length > 0);
  }

  constructor() {
    afterNextRender(() => {
      this.subscribeToControlChanges();
      this.createEditor();
    });

    effect(() => {
      const editable = this.canEdit();
      if (!this.initialized()) return;
      untracked(() => this.syncEditorEditableState(editable));
    });

    effect(() => {
      const value = this.value();
      if (!this.initialized()) return;
      untracked(() => this.syncExternalValue(value));
    });

    // ProseMirror owns the contenteditable element, so its accessibility attributes are set through the editor
    effect(() => {
      const attributes = this.editorAttributes();
      if (!this.initialized()) return;
      untracked(() => this.editor?.setOptions({ editorProps: { attributes } }));
    });

    // The link field only exists once the editor row has rendered, so focus it when it appears
    effect(() => this.linkInput()?.nativeElement.focus());

    this.destroyRef.onDestroy(() => this.editor?.destroy());
  }

  writeValue(value: string | null | undefined): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: ChangeFn<string | undefined>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  public getPlainText(): string {
    if (!this.editor) return '';

    try {
      const htmlContent = this.editor.getHTML();
      if (!htmlContent) return '';

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      return this.getTextWithSpaces(doc.body).replace(/\s+/g, ' ').trim();
    } catch {
      return '';
    }
  }

  protected runToolbarItem(item: JoiiRichTextToolbarItem): void {
    if (!this.editor || !this.canEdit()) return;

    // Building a chain with focus() refocuses the editor even if it never runs, which would steal focus from the link field
    if (item === 'link') {
      this.openLinkEditor();
      return;
    }

    const chain = this.editor.chain().focus();
    switch (item) {
      case 'heading':
        chain.toggleHeading({ level: 2 }).run();
        break;
      case 'bold':
        chain.toggleBold().run();
        break;
      case 'italic':
        chain.toggleItalic().run();
        break;
      case 'strike':
        chain.toggleStrike().run();
        break;
      case 'bulletList':
        chain.toggleBulletList().run();
        break;
      case 'orderedList':
        chain.toggleOrderedList().run();
        break;
    }
  }

  protected applyLink(): void {
    const text = this.linkHref().trim();
    if (!this.editor) return;

    if (!text) {
      this.removeLink();
      return;
    }

    const href = withScheme(text);
    const editor = this.editor;
    // Link validation rejects javascript: and similar, so check before inserting anything
    if (!editor.can().setLink({ href })) {
      this.linkError.set('Enter a web address, such as https://joii.vet');
      return;
    }

    const { empty } = editor.state.selection;
    if (empty && !editor.isActive('link')) {
      // Nothing to turn into a link, so insert the address itself as the link text
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    this.closeLinkEditor();
  }

  protected removeLink(): void {
    this.editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    this.closeLinkEditor();
  }

  protected closeLinkEditor(): void {
    this.linkEditorOpen.set(false);
    this.linkHref.set('');
    this.linkError.set(null);
  }

  protected toggleFullscreen(): void {
    this.fullscreen.update((fullscreen) => !fullscreen);
    this.editor?.commands.focus();
  }

  protected onMouseEnter(): void {
    if (this.readonly() || this.isDisabled()) return;
    this.hovered.set(true);
  }

  protected onMouseLeave(): void {
    this.hovered.set(false);
  }

  private openLinkEditor(): void {
    this.linkHref.set(this.editor?.getAttributes('link')['href'] ?? '');
    this.linkEditorOpen.set(true);
  }

  private createEditor(): void {
    if (this.editor) return;

    const element = this.editorElement()?.nativeElement;
    if (!element) return;

    const value = this.value();
    this.editor = new Editor({
      element,
      extensions: createRichTextExtensions(),
      ...this.toEditorContent(value),
      editable: this.canEdit(),
      editorProps: { attributes: this.editorAttributes() },
      // Only edits count: StarterKit's TrailingNode appends a paragraph after a note ending in a list, heading or table
      // on the first focus or click, which would otherwise re-serialise the note and mark the form dirty
      onUpdate: ({ transaction }) => {
        if (transaction.docChanged) this.onEditorValueChange();
      },
      onTransaction: () => this.refreshToolbarState(),
      onFocus: () => this.onFocus(),
      onBlur: () => this.onBlur(),
    });
    this.lastSyncedEditorValue = value;
    this.initialized.set(true);
    this.cdr.markForCheck();
  }

  // Values containing HTML tags are treated as HTML (content saved with outputType="html"); everything else is markdown
  private toEditorContent(value: string): { content: string; contentType: 'html' | 'markdown' } {
    const safeValue = typeof value === 'string' ? value : '';
    return isRichTextHTML(safeValue)
      ? { content: sanitizeRichTextHTML(safeValue), contentType: 'html' }
      : { content: safeValue, contentType: 'markdown' };
  }

  private editorAttributes(): Record<string, string> {
    // hasError isn't a signal, so depend on the control status directly for aria-invalid to follow it
    this.controlStatusVersion();
    const attributes: Record<string, string> = {
      id: this.inputId(),
      role: 'textbox',
      'aria-multiline': 'true',
      'aria-invalid': String(this.hasError),
      'aria-disabled': String(this.isDisabled()),
      'aria-readonly': String(this.readonly()),
    };
    const ariaLabel = this.ariaLabel() || (this.showLabel() ? '' : this.label());
    if (ariaLabel) attributes['aria-label'] = ariaLabel;
    if (!this.ariaLabel() && this.showLabel()) attributes['aria-labelledby'] = this.labelId();
    if (this.ariaDescribedby()) attributes['aria-describedby'] = this.ariaDescribedby()!;
    return attributes;
  }

  private onEditorValueChange(): void {
    if (!this.editor) return;

    if (!this.canEdit()) {
      this.loadEditorValue(this.value());
      return;
    }

    const nextValue = this.getEditorValue();
    if (nextValue === this.value()) return;

    this.lastSyncedEditorValue = nextValue;
    this.value.set(nextValue);
    this.onChange?.(nextValue);
  }

  private onFocus(): void {
    if (this.isDisabled() || this.readonly()) {
      this.editor?.commands.blur();
      return;
    }

    this.focused.set(true);
    this.touched.emit();
  }

  private onBlur(): void {
    this.focused.set(false);
    this.onTouched?.();
    this.touched.emit();
  }

  private syncEditorEditableState(editable: boolean): void {
    if (!this.editor) return;

    this.editor.setEditable(editable, false);
    if (!editable) {
      this.focused.set(false);
      this.hovered.set(false);
      this.closeLinkEditor();
      this.editor.commands.blur();
    }
  }

  private syncExternalValue(value: string): void {
    const safeValue = typeof value === 'string' ? value : '';
    if (safeValue === this.lastSyncedEditorValue) return;

    this.loadEditorValue(safeValue);
  }

  private loadEditorValue(value: string): void {
    if (!this.editor) return;

    this.lastSyncedEditorValue = value;
    if (this.getEditorValue() === value) return;

    const { content, contentType } = this.toEditorContent(value);
    this.editor.commands.setContent(content, { contentType, emitUpdate: false });
  }

  private getEditorValue(): string {
    if (!this.editor || this.editor.isEmpty) return '';
    // Tiptap leaves blank lines after a trailing list, which would otherwise be saved with every note
    return this.outputType() === 'html'
      ? sanitizeRichTextHTML(this.editor.getHTML())
      : this.editor.getMarkdown().replace(/\n+$/, '');
  }

  private refreshToolbarState(): void {
    if (!this.toolbar() || !this.editor) return;

    const editor = this.editor;
    this.toolbarState.set({
      heading: editor.isActive('heading', { level: 2 }),
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      strike: editor.isActive('strike'),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      link: editor.isActive('link'),
    });
  }

  private hasEditorValue(value: string): boolean {
    if (!value.trim()) return false;
    if (!isRichTextHTML(value)) return true;
    // Images, tables and rules are content even without any text
    if (/<(img|table|hr)\b/i.test(value)) return true;

    return (
      value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0
    );
  }

  private getTextWithSpaces(node: Node): string {
    let text = '';
    node.childNodes.forEach((childNode) => {
      if (childNode.nodeType === Node.TEXT_NODE) {
        text += childNode.textContent;
        return;
      }

      text += this.getTextWithSpaces(childNode);
      if (childNode.nextSibling) {
        text += ' ';
      }
    });
    return text;
  }

  private subscribeToControlChanges(): void {
    const ngControl = this.injector.get(NgControl, null);
    const control = ngControl?.control;
    if (!control) return;

    const bumpVersion = () => {
      this.controlStatusVersion.update((v) => v + 1);
      this.cdr.markForCheck();
    };

    control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
    control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
    this.formDirective?.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
  }

  private getShowError(
    focused: boolean,
    control?: AbstractControl,
    formGroupDirective?: FormGroupDirective,
    showDirtyErrors = false
  ): boolean {
    if (formGroupDirective) {
      return (
        (formGroupDirective.submitted ?? false) ||
        (showDirtyErrors && (control?.dirty ?? false)) ||
        (focused && (control?.dirty ?? false) && (control?.touched ?? false))
      );
    }
    return focused || (control?.touched ?? false) || (control?.dirty ?? false);
  }

  private getControlErrorMessageFromErrors(errors: Record<string, unknown>): string {
    for (const key of Object.keys(errors)) {
      const value = errors[key];
      if (value && typeof value === 'object' && typeof (value as { message?: string }).message === 'string') {
        return (value as { message: string }).message;
      }
    }
    if (errors['required'] ?? errors['requiredTrue']) return 'Field is required';
    if (errors['minlength'])
      return `Minimum length is ${(errors['minlength'] as { requiredLength: number }).requiredLength} characters`;
    if (errors['maxlength'])
      return `Maximum length is ${(errors['maxlength'] as { requiredLength: number }).requiredLength} characters`;
    return 'Field is invalid';
  }

  protected readonly Math = Math;
}

// "joii.vet/help" or "staging.joii.vet:8443/x" would otherwise be saved as a relative link or read as a scheme
function withScheme(href: string): string {
  if (/^(\/|#|(mailto|tel|sms):)/i.test(href)) return href;
  // host:port (e.g. localhost:4200) looks like a scheme but isn't; any other scheme is kept for link validation to judge
  const isHostAndPort = /^[^/:?#]+:\d+([/?#]|$)/.test(href);
  return !isHostAndPort && /^[a-z][a-z\d+.-]*:/i.test(href) ? href : `https://${href}`;
}

function emptyToolbarState(): Record<JoiiRichTextToolbarItem, boolean> {
  return {
    heading: false,
    bold: false,
    italic: false,
    strike: false,
    bulletList: false,
    orderedList: false,
    link: false,
  };
}
